import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Check, Circle, X, Search } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=200';

// El tamaño interno 40 se muestra como 35ml en toda la tienda
const sizeLabel = (ml) => (ml === 40 ? 35 : ml);

const Step = ({ done, children, testId }) => (
  <li className="flex items-start gap-2 text-xs md:text-sm" data-testid={testId}>
    {done ? (
      <Check size={16} className="mt-0.5 flex-shrink-0 text-green-600" />
    ) : (
      <Circle size={16} className="mt-0.5 flex-shrink-0 text-brand-black/30" />
    )}
    <span className={done ? 'text-brand-black' : 'text-brand-black/70'}>{children}</span>
  </li>
);

/**
 * Selector del perfume de 10ml de regalo. Cada perfume solo puede elegirse tantas veces
 * como 10ml haya en inventario (`available`); el servidor vuelve a validar.
 */
const MiniPicker = ({ required, initial, onClose, onConfirm, saving }) => {
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState(initial);
  const [query, setQuery] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/cart/mini-options`)
      .then((r) => (r.ok ? r.json() : { options: [] }))
      .then((d) => !cancelled && setOptions(d.options || []))
      .catch(() => !cancelled && setOptions([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!options) return [];
    if (!q) return options;
    return options.filter((o) =>
      `${o.product_code} ${o.name} ${o.brand || ''}`.toLowerCase().includes(q)
    );
  }, [options, query]);

  const countOf = (id) => selected.filter((x) => x === id).length;

  const toggle = (option) => {
    const id = option.product_id;
    if (required === 1) {
      setSelected([id]);
      return;
    }
    if (countOf(id) > 0) {
      // quitar una unidad
      const idx = selected.lastIndexOf(id);
      setSelected(selected.filter((_, i) => i !== idx));
    } else if (selected.length < required && countOf(id) < option.available) {
      setSelected([...selected, id]);
    }
  };

  const byId = (id) => (options || []).find((o) => o.product_id === id);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="mini-picker">
      <div className="absolute inset-0 bg-brand-black/60" onClick={onClose} />
      <div className="relative bg-brand-bone w-full sm:max-w-lg max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between p-4 border-b border-brand-black/10">
          <div>
            <h3 className="font-display text-lg text-brand-black">Elige tu perfume de 10ml</h3>
            <p className="text-xs text-brand-black/60">
              {required > 1
                ? `Elige ${required} (llevas ${selected.length}). Es un regalo de tu promoción.`
                : 'Es un regalo de tu promoción.'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-brand-black/50 hover:text-brand-black" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="p-3 border-b border-brand-black/10">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-black/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, marca o código"
              className="w-full pl-8 pr-2 py-2 bg-white border border-brand-black/20 focus:border-brand-cobalt focus:outline-none text-sm rounded text-brand-black"
              data-testid="mini-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-3" data-lenis-prevent>
          {options === null ? (
            <p className="text-center text-sm text-brand-black/50 py-8">Cargando...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-brand-black/50 py-8">No hay perfumes de 10ml que coincidan.</p>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {filtered.map((o) => {
                const count = countOf(o.product_id);
                const isSelected = count > 0;
                const full = !isSelected && selected.length >= required && required > 1;
                return (
                  <li key={o.product_id}>
                    <button
                      type="button"
                      onClick={() => toggle(o)}
                      disabled={full}
                      className={`w-full text-left flex gap-2 p-2 rounded border transition-colors ${
                        isSelected
                          ? 'border-brand-cobalt bg-brand-cobalt/5'
                          : 'border-brand-black/15 bg-white hover:border-brand-cobalt/50'
                      } ${full ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`mini-option-${o.product_code}`}
                    >
                      <img
                        src={o.image || FALLBACK_IMAGE}
                        alt=""
                        className="w-12 h-12 object-cover rounded bg-brand-black/5 flex-shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[10px] text-brand-black/50 truncate">{o.product_code}</span>
                        <span className="block text-xs font-medium text-brand-black leading-tight line-clamp-2">{o.name}</span>
                        {o.brand && <span className="block text-[10px] text-brand-black/50 truncate">{o.brand}</span>}
                      </span>
                      {isSelected && <Check size={16} className="text-brand-cobalt flex-shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-4 border-t border-brand-black/10">
          {selected.length > 0 && (
            <p className="text-xs text-brand-black/60 mb-2 truncate">
              {selected.map((id) => byId(id)?.name).filter(Boolean).join(' • ')}
            </p>
          )}
          <button
            onClick={() => onConfirm(selected)}
            disabled={selected.length !== required || saving}
            className="w-full brand-btn disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="mini-confirm"
          >
            {saving ? 'Guardando...' : 'Confirmar mi regalo'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Tarjeta de progreso de la promo de regalos (5x3). Todo lo muestra desde `gifts`, que lo
 * calcula el servidor; aquí no se decide ningún precio ni derecho a regalo.
 */
const GiftPromoCard = () => {
  const { gifts, setGiftPicks } = useCart();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!gifts || gifts.qualifying_quantity < 1) return null;

  const { buy_quantity: buyQty, qualifying_quantity: have, applied } = gifts;
  const pocket = sizeLabel(gifts.pocket_size_ml);

  const handleConfirm = async (ids) => {
    setSaving(true);
    const ok = await setGiftPicks(ids);
    setSaving(false);
    if (ok) setPickerOpen(false);
  };

  // Aún no llega a la cantidad mínima: invitación
  if (!applied) {
    return (
      <div className="p-3 md:p-4 rounded-lg border border-brand-cobalt/30 bg-brand-cobalt/5" data-testid="gift-promo-teaser">
        <div className="flex items-start gap-3">
          <Gift size={20} className="text-brand-cobalt flex-shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm text-brand-black">
            <p className="font-medium">Promo {gifts.promo_name}: te regalamos 2 perfumes</p>
            <p className="text-brand-black/70 mt-0.5">
              Llevas {have} de {buyQty} perfumes de {gifts.buy_size_ml}ml. Con {buyQty} te llevas uno de {pocket}ml y uno de 10ml gratis.{' '}
              <Link to="/products" className="text-brand-cobalt underline">Ver perfumes</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const pocketDone = gifts.pocket_missing === 0;
  const picksNeeded = gifts.picks_required;

  return (
    <div className="p-3 md:p-4 rounded-lg border border-brand-cobalt/40 bg-brand-cobalt/5" data-testid="gift-promo-card">
      <div className="flex items-center gap-2 mb-3">
        <Gift size={20} className="text-brand-cobalt" />
        <p className="font-display text-base md:text-lg text-brand-black">Promo {gifts.promo_name}: tus regalos</p>
      </div>

      <ul className="space-y-2">
        <Step done testId="gift-step-100">
          {have} perfumes de {gifts.buy_size_ml}ml en tu carrito
        </Step>

        <Step done={pocketDone} testId="gift-step-pocket">
          {pocketDone ? (
            <>Perfume de {pocket}ml de regalo incluido</>
          ) : (
            <>
              Agrega {gifts.pocket_missing} perfume{gifts.pocket_missing > 1 ? 's' : ''} de {pocket}ml y te {gifts.pocket_missing > 1 ? 'los' : 'lo'} regalamos.{' '}
              <Link to="/products" className="text-brand-cobalt underline">Elegir</Link>
            </>
          )}
        </Step>

        {picksNeeded > 0 && (
          <Step done={gifts.complete} testId="gift-step-mini">
            {gifts.complete ? (
              <>
                10ml de regalo:{' '}
                <strong>{gifts.picks.map((p) => p.name).join(', ')}</strong>{' '}
                <button
                  onClick={() => setPickerOpen(true)}
                  className="text-brand-cobalt underline"
                  data-testid="gift-change-btn"
                >
                  Cambiar
                </button>
              </>
            ) : (
              <>
                Elige {picksNeeded > 1 ? `tus ${picksNeeded} perfumes` : 'tu perfume'} de 10ml de regalo{' '}
                <span className="text-brand-magenta">(obligatorio para continuar)</span>
              </>
            )}
          </Step>
        )}
      </ul>

      {picksNeeded > 0 && !gifts.complete && (
        <button
          onClick={() => setPickerOpen(true)}
          className="w-full mt-3 brand-btn"
          data-testid="gift-pick-btn"
        >
          Elegir mi perfume de 10ml
        </button>
      )}

      {pickerOpen && (
        <MiniPicker
          required={picksNeeded}
          initial={gifts.picks.map((p) => p.product_id)}
          onClose={() => setPickerOpen(false)}
          onConfirm={handleConfirm}
          saving={saving}
        />
      )}
    </div>
  );
};

export default GiftPromoCard;
